import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './Header.css';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const Header = () => {
  const { language, changeLanguage } = useLanguage();
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const languageRef = useRef(null);
  const selectorRef = useRef(null);
  const dropdownRef = useRef(null);
  const headerRef = useRef(null);
  const t = translations[language];

  // Проверка размера экрана
  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const languages = [
    { code: 'ru', name: 'Русский', short: 'RU' },
    { code: 'kz', name: 'Қазақша', short: 'KZ' },
    { code: 'en', name: 'English', short: 'EN' },
  ];

  const currentLanguage = languages.find(lang => lang.code === language) || languages[0];

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    setIsLanguageOpen(false);
  };

  const handleLanguageToggle = () => {
    if (!isLanguageOpen && selectorRef.current) {
      const rect = selectorRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left,
      });
    }
    setIsLanguageOpen(!isLanguageOpen);
  };

  // Закрытие языкового меню при клике вне его
  useEffect(() => {
    if (!isLanguageOpen) return;

    const handleClickOutside = (event) => {
      const isClickInsideSelector = languageRef.current && languageRef.current.contains(event.target);
      const isClickInsideDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);
      
      if (!isClickInsideSelector && !isClickInsideDropdown) {
        setIsLanguageOpen(false);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isLanguageOpen]);

  // Переключение бургер-меню
  const toggleMenu = () => {
    setIsMenuOpen(prev => !prev);
  };

  // Закрытие меню при клике на пункт
  const handleMenuItemClick = () => {
    setIsMenuOpen(false);
  };

  // Мобильное меню (рендерится через portal)
  const MobileMenu = () => {
    if (!isMenuOpen || isDesktop) return null;
    
    return createPortal(
      <nav className="mobile-menu">
        <a href="#about" className="mobile-menu-item" onClick={handleMenuItemClick}>{t.menuAbout}</a>
        <a href="#programs" className="mobile-menu-item" onClick={handleMenuItemClick}>{t.menuPrograms}</a>
        <a href="#training" className="mobile-menu-item" onClick={handleMenuItemClick}>{t.menuTraining}</a>
        <a href="#partners" className="mobile-menu-item" onClick={handleMenuItemClick}>{t.menuPartners}</a>
        <a href="#contacts" className="mobile-menu-item" onClick={handleMenuItemClick}>{t.menuContacts}</a>
      </nav>,
      document.body
    );
  };

  return (
    <section className="hero-section">
      <header ref={headerRef} className={`header ${scrolled ? 'scrolled' : ''}`}>
        <div className="header-container">
          <div className="header-logo">
            <img 
              src="/logos/shai-pro.png" 
              alt="shai.pro" 
              className="header-logo-image"
            />
          </div>
          
          {/* Бургер-кнопка для мобильных */}
          {!isDesktop && (
            <button 
              className="burger-menu-button"
              onClick={toggleMenu}
              aria-label="Toggle menu"
              aria-expanded={isMenuOpen}
              type="button"
            >
              <span className={`burger-line ${isMenuOpen ? 'open' : ''}`}></span>
              <span className={`burger-line ${isMenuOpen ? 'open' : ''}`}></span>
              <span className={`burger-line ${isMenuOpen ? 'open' : ''}`}></span>
            </button>
          )}
          
          {/* Десктопное меню (inline) */}
          {isDesktop && (
            <nav className="header-menu desktop">
              <a href="#about" className="menu-item">{t.menuAbout}</a>
              <a href="#programs" className="menu-item">{t.menuPrograms}</a>
              <a href="#training" className="menu-item">{t.menuTraining}</a>
              <a href="#partners" className="menu-item">{t.menuPartners}</a>
              <a href="#contacts" className="menu-item">{t.menuContacts}</a>
            </nav>
          )}
          
          <div className="header-actions">
            <div className="language-selector-wrapper" ref={languageRef}>
              <div 
                ref={selectorRef}
                className="language-selector"
                onClick={handleLanguageToggle}
              >
                <span className="language-text">{currentLanguage.short}</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="#2C2B2F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              {isLanguageOpen && createPortal(
                <div 
                  ref={dropdownRef}
                  className="language-dropdown"
                  style={{
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                  }}
                >
                  {languages.map((lang) => (
                    <div
                      key={lang.code}
                      className={`language-option ${language === lang.code ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLanguageChange(lang.code);
                      }}
                    >
                      {lang.name}
                    </div>
                  ))}
                </div>,
                document.body
              )}
            </div>
            <button 
              className="cta-button"
              onClick={() => window.location.href = 'https://lms.shai.academy/auth/login'}
            >
              {t.buttonLogin}
            </button>
          </div>
        </div>
      </header>
      
      {/* Мобильное меню через portal */}
      <MobileMenu />
    </section>
  );
};

export default Header;
